import React, { useState } from 'react';
import { Lightbulb, X, Plus, AlertTriangle } from 'lucide-react';

/**
 * V6.7 — Toast discret post-import qui détecte les triggers et propose des objectifs.
 *
 * Apparait en bas à droite après un import si des triggers sont détectés.
 * L'utilisateur peut :
 *  - Cliquer "Voir suggestions" → modal complet avec détail
 *  - Ignorer (X) → toast disparaît, ne réapparait pas pour ces mêmes triggers
 *
 * Props :
 *  - triggers : array de triggers détectés
 *  - onAccept : callback(suggestion) pour créer le bloc
 *  - onDismiss : callback() quand toast fermé
 */

export default function ImportSuggestionsToast({ triggers, onAccept, onDismiss, currentGameCount }) {
  const [showModal, setShowModal] = useState(false);
  const [accepted, setAccepted] = useState(new Set());

  if (!triggers || triggers.length === 0) return null;

  const severityCount = {
    high: triggers.filter(t => t.severity === 'high').length,
    medium: triggers.filter(t => t.severity === 'medium').length
  };

  const handleAccept = async (trigger) => {
    const block = {
      title: trigger.suggestion.title,
      description: trigger.suggestion.description,
      metric_type: trigger.suggestion.metric_type,
      metric_operator: trigger.suggestion.metric_operator,
      metric_target: trigger.suggestion.metric_target,
      target_games: trigger.suggestion.target_games,
      filter_champion: trigger.suggestion.filter_champion || null,
      started_at_game_count: currentGameCount,
      status: 'active'
    };
    await onAccept(block);
    setAccepted(prev => new Set([...prev, trigger.id]));
  };

  return (
    <>
      {/* Toast discret en bas à droite */}
      {!showModal && (
        <div
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 max-w-sm bg-zinc-950 border border-cyan-400/40 shadow-xl"
          style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 0 20px rgba(34, 211, 238, 0.15)' }}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Lightbulb size={18} className="text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-white">
                  {triggers.length} suggestion{triggers.length > 1 ? 's' : ''} détectée{triggers.length > 1 ? 's' : ''}
                </div>
                <div className="text-[10px] text-zinc-500 font-mono mt-1">
                  {severityCount.high > 0 && (
                    <span className="text-rose-400 mr-2">{severityCount.high} prio. haute</span>
                  )}
                  {severityCount.medium > 0 && (
                    <span className="text-amber-400">{severityCount.medium} prio. moy.</span>
                  )}
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="mt-2 text-xs font-bold tracking-wider text-cyan-400 hover:text-cyan-300 transition"
                >
                  VOIR LES SUGGESTIONS →
                </button>
              </div>
              <button
                onClick={onDismiss}
                className="flex-shrink-0 text-zinc-500 hover:text-white transition"
                title="Ignorer"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal complet */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
          <div
            className="bg-zinc-950 border border-cyan-400/30 w-full max-w-2xl my-2 md:my-8"
            style={{ boxShadow: '0 0 40px rgba(34, 211, 238, 0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-zinc-800 p-5 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-cyan-400 uppercase flex items-center gap-1">
                    <Lightbulb size={10} /> Suggestions Post-Import
                  </div>
                  <div className="font-black text-lg">Tendances détectées</div>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="p-5">
              <div className="text-xs text-zinc-400 mb-4 font-mono">
                Basé sur tes <strong className="text-cyan-400">10 dernières games</strong>. Choisis les objectifs qui résonnent avec ton ressenti.
              </div>

              <div className="space-y-4">
                {triggers.map(trigger => {
                  const isAccepted = accepted.has(trigger.id);
                  const severityColor = trigger.severity === 'high' ? 'rose' : 'amber';
                  return (
                    <div
                      key={trigger.id}
                      className={`border p-4 transition ${
                        isAccepted
                          ? 'border-emerald-400/40 bg-emerald-400/5'
                          : `border-${severityColor}-400/40 bg-${severityColor}-400/5`
                      }`}
                    >
                      {/* Trigger header */}
                      <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle size={14} className={trigger.severity === 'high' ? 'text-rose-400' : 'text-amber-400'} />
                        <div className="flex-1 min-w-0">
                          <div className={`font-bold text-sm ${trigger.severity === 'high' ? 'text-rose-400' : 'text-amber-400'}`}>
                            {trigger.title}
                          </div>
                          <div className="text-xs text-zinc-300 mt-1">{trigger.detail}</div>
                        </div>
                      </div>

                      {/* Suggestion */}
                      <div className="border-t border-zinc-800 pt-3 mt-3">
                        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold mb-2">Objectif suggéré</div>
                        <div className="font-bold text-white mb-1">{trigger.suggestion.title}</div>
                        <div className="text-xs text-zinc-400 mb-3">{trigger.suggestion.description}</div>

                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[10px] font-mono text-zinc-500">
                            Cible : <span className="text-cyan-400 font-bold">{trigger.suggestion.metric_operator} {trigger.suggestion.metric_target}</span> sur {trigger.suggestion.target_games} games
                            {trigger.suggestion.filter_champion && (
                              <span className="text-fuchsia-400"> · {trigger.suggestion.filter_champion}</span>
                            )}
                          </div>
                          {isAccepted ? (
                            <span className="text-emerald-400 text-xs font-bold tracking-wider">✓ CRÉÉ</span>
                          ) : (
                            <button
                              onClick={() => handleAccept(trigger)}
                              className="bg-gradient-to-r from-cyan-400 to-emerald-400 text-black px-3 py-1.5 font-black text-xs tracking-wider hover:opacity-90 transition flex items-center gap-1"
                            >
                              <Plus size={11} /> ACCEPTER
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-zinc-800 p-4 flex gap-2">
              <button
                onClick={() => { setShowModal(false); onDismiss(); }}
                className="flex-1 border border-zinc-800 text-zinc-400 p-3 hover:border-zinc-700 transition font-semibold tracking-wider text-sm"
              >
                FERMER
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
